import { QueryObserverResult, useQuery } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { useDatasetsDataQueryObserver } from '@global/hooks/queries/useDatasetsDataQueryObserver'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { rowPropertyAliases } from '../handsontable/read-grid/readGridToolbar/components/toggleColumns/config'
import { trackEvent, EventTrackingEventType } from 'lib/eventTracking'
import { t } from '@lingui/core/macro'

interface ExportDatasetDataResponse {
  url: string
}

/**
 * Custom hook that uses React Query to export dataset data.
 * This can be used in any child component of DatasetQueryContext
 * that has access to the datasetId from the URL.
 *
 * @returns {QueryObserverResult<ExportDatasetDataResponse, Error>} React Query result
 */
export function useExportDatasetData(
  exportCurrentView: boolean,
  exportConfigurationId: number,
  fileType: string,
  fileName: string,
  exportWithRelationships = false,
): QueryObserverResult<ExportDatasetDataResponse, Error> {
  const dispatch = useDispatch()
  const { makeQueryOptionsDto, queryOptionsByFieldId, getDatasetId } = useDatasetQueryContext()

  const [CREATIVE_CLASSIFICATION_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.CREATIVE_CLASSIFICATION]),
  )

  const id = getDatasetId() ?? ''
  const { data: dataset } = useDatasetsDataQueryObserver({ datasetId: Number(id) })
  const url = `/api/core/v1/datasets/${id}/exports/`

  async function exportDatasetData(): Promise<ExportDatasetDataResponse | null> {
    trackEvent(EventTrackingEventType.DATA_EXPORTED, {
      exportName: 'Template Export',
    })

    const queryOptionsDto = makeQueryOptionsDto({ optionsByFieldId: queryOptionsByFieldId })

    if (exportCurrentView && dataset?.filteredColumns) {
      const normalColumns = dataset.filteredColumns.filter(column => !column.isClassificationColumn)
      const select = normalColumns.reduce<Array<number | string>>((memo, column) => {
        if (column.governanceType === 'property') {
          queryOptionsDto.includeRowProperties = true
          const db = column.rowPropertyName
            ? rowPropertyAliases.get(column.rowPropertyName)?.db
            : ''
          memo.push(db ?? '')
        } else {
          memo.push(column.fieldId)
        }
        return memo
      }, [])
      queryOptionsDto.select = select
    } else {
      queryOptionsDto.select = ['*']
      queryOptionsDto.includeRowProperties = true
    }

    if (exportWithRelationships) {
      queryOptionsDto.includeRelationships = true
    }

    if (CREATIVE_CLASSIFICATION_ENABLED) {
      const classificationColumns =
        (dataset?.filteredColumns ?? dataset?.allColumns)?.filter(
          column => column.isClassificationColumn,
        ) ?? []

      if (classificationColumns.length > 0) {
        queryOptionsDto.classificationCategoryIds = classificationColumns.map(
          column => column.fieldId,
        )
        queryOptionsDto.includeClassification = true
      }
    }

    try {
      dispatch(
        showSnackbar({
          message: t`Successfully started download`,
          type: 'SUCCESS',
          duration: 5000,
        }),
      )
      const queryParams = `?fileType=${fileType}&fileName=${fileName.replace(/[[\]|]/g, '')}`
      const { data } = await api.post(
        `${url}${
          !isNaN(exportConfigurationId)
            ? `${queryParams}&format=${exportConfigurationId}`
            : queryParams
        }`,
        queryOptionsDto,
      )
      const downloadUrl = `${url}/${data.uuid}?jwt=${data.jwt}`
      download(downloadUrl)
      return data
    } catch {
      dispatch(
        showSnackbar({
          message: t`Failed to download`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
      return null
    }

    function download(downloadUrl): void {
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', 'export.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  return useQuery({
    queryKey: ['exportDatasetData', id],
    queryFn: exportDatasetData,
    enabled: false,
  })
}
